import * as log from './log';

/////////////////////////////////////////////////////////
// Ortto (email marketing) integration.                //
/////////////////////////////////////////////////////////

// Ortto's website tracking code. The tracking key is public by design (it ends
// up in the page source anyway), unlike the private API key of a "Custom API"
// data source, which must never be added to the client.
const ORTTO_TRACKING_KEY = 'YuOsFOXpCjNVr_fsZXRoeQ';
const ORTTO_CAPTURE_URL = 'https://ot.ethy.co.uk/';

// Custom activity created in Ortto ("Signed up"). Ortto prefixes custom
// activity IDs with `act:cm:`.
const SIGNED_UP_ACTIVITY_ID = 'act:cm:signed-up';

// Ortto person field IDs. `str:cm:` is the prefix for custom text fields, so
// the "User type" field created in Ortto becomes `str:cm:user-type`.
// The "Shoppers" and "Verified Sellers" audiences in Ortto filter on this field.
const FIELD_EMAIL = 'str::email';
const FIELD_FIRST_NAME = 'str::first';
const FIELD_LAST_NAME = 'str::last';
const FIELD_EMAIL_PERMISSION = 'bol::p';
const FIELD_USER_TYPE = 'str:cm:user-type';

let isOrttoLoaded = false;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Load Ortto's tracking code. This is the snippet from Ortto
 * (Settings → Data sources → Website / Tracking code), so that page views are
 * tracked and `trackOrttoSignup` can send sign-ups to Ortto.
 */
export const loadOrtto = () => {
  if (!isBrowser() || isOrttoLoaded) {
    return;
  }
  isOrttoLoaded = true;

  window.ap3c = window.ap3c || {};
  const ap3c = window.ap3c;
  ap3c.cmd = ap3c.cmd || [];
  ap3c.cmd.push(function() {
    ap3c.init(ORTTO_TRACKING_KEY, ORTTO_CAPTURE_URL);
    ap3c.track({ v: 0 });
  });
  ap3c.activity = function(act) {
    ap3c.act = ap3c.act || [];
    ap3c.act.push(act);
  };

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `${ORTTO_CAPTURE_URL}app.js`;
  document.head.appendChild(script);
};

/**
 * Send a new sign-up to Ortto, so that the person lands in the "Shoppers" or
 * "Verified Sellers" audience based on their user type.
 *
 * This never throws: a failure to reach Ortto must not break sign-up.
 *
 * @param {Object} currentUser the signed-up user entity
 */
export const trackOrttoSignup = currentUser => {
  try {
    const { email, profile } = currentUser?.attributes || {};
    const { firstName, lastName, publicData } = profile || {};
    const userType = publicData?.userType;

    if (!isBrowser() || typeof window.ap3c?.activity !== 'function' || !email) {
      return;
    }

    window.ap3c.activity({
      activity_id: SIGNED_UP_ACTIVITY_ID,
      fields: {
        [FIELD_EMAIL]: email,
        ...(firstName ? { [FIELD_FIRST_NAME]: firstName } : {}),
        ...(lastName ? { [FIELD_LAST_NAME]: lastName } : {}),
        ...(userType ? { [FIELD_USER_TYPE]: userType } : {}),
        // Both shoppers and sellers are subscribed to marketing emails on sign-up.
        [FIELD_EMAIL_PERMISSION]: true,
      },
    });
  } catch (e) {
    log.error(e, 'ortto-signup-tracking-failed');
  }
};
